import dayjs from 'dayjs'
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { payAndMakeEventBooking } from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, getCountryCode, platformWeb } from 'src/consumer/services'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'
import { currency_list } from 'src/consumer/constants/currencyList'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
  postDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { saveClinicWalkinBookingHandoff } from 'src/shared/clinicWalkinBookingHandoff'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { CPBPOMBookAndPayButton } from './ClinicBrandPageStyles'
import { getWalkinBookableSlot } from './ClinicWalkinPageSlotUtils'

const ClubbedAuthModal = React.lazy(() =>
  import('src/consumer/components/SignIn/ClubbedAuthComponents').then((module) => ({
    default: module.ClubbedAuthModal,
  })),
)

const WALKIN_BOOKING_SOURCE = 'clinic_walkin_qr'
const CODE_EXPIRED = 49081
const LAST_PATIENT_PROFILE_KEY = 'patientPortal.currentProfileId'
const WALKIN_FEE_CURRENCY_PREFIX = {
  inr: 'INR ',
  usd: '$',
  eur: 'EUR ',
  gbp: 'GBP ',
}
const UNAVAILABLE_EVENT_TITLE = 'Appointment type unavailable'

const WALKIN_FRAME_STYLE = {
  width: '100%',
  minHeight: '100svh',
  margin: 0,
  padding: '16px',
  background: '#f7f8fb',
  fontFamily: 'Outfit, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#111827',
}

function WalkinFrame({ children, centered = false }) {
  return (
    <main
      style={{
        ...WALKIN_FRAME_STYLE,
        justifyContent: centered ? 'center' : 'flex-start',
      }}
    >
      {children}
    </main>
  )
}

function WalkinResolvingState() {
  return (
    <WalkinFrame centered>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          padding: 18,
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          background: '#ffffff',
        }}
      >
        <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
          Clinic walk-in booking
        </div>
        <div
          style={{
            width: 56,
            height: 56,
            margin: '18px auto 14px',
            borderRadius: 8,
            background: '#eef2ff',
          }}
        />
        <div
          style={{
            height: 18,
            width: '68%',
            margin: '0 auto',
            borderRadius: 4,
            background: '#e5e7eb',
          }}
        />
        <div
          style={{
            height: 12,
            width: '42%',
            margin: '10px auto 0',
            borderRadius: 4,
            background: '#eef2f7',
          }}
        />
        <div style={{ marginTop: 18, display: 'grid', gap: 8 }}>
          <div style={{ height: 48, borderRadius: 8, background: '#f3f4f6' }} />
          <div style={{ height: 76, borderRadius: 8, background: '#f9fafb' }} />
        </div>
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: '#6b7280' }}>
          Loading your appointment details...
        </div>
      </div>
    </WalkinFrame>
  )
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function unwrapResponse(response) {
  if (response?.response) {
    return { status: response.response.status, data: response.response.data }
  }
  return response
}

function currencyPrefix(...values) {
  const code = String(firstPresent(...values) || '').toLowerCase()
  const fromList = currency_list?.find((item) => item?.code === code)?.symbol
  return firstPresent(fromList, WALKIN_FEE_CURRENCY_PREFIX[code], code ? `${code.toUpperCase()} ` : '')
}

function formatAmount(value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
  }).format(numericValue)
}

function formatFeeValue(value, currencyId, currencySymbol) {
  if (value === undefined || value === null || value === '') return ''
  const prefix = firstPresent(currencySymbol, currencyPrefix(currencyId), '')
  return `${prefix}${formatAmount(value)}`
}

function getWalkinEventTitle(event, fallback = '') {
  const title = firstPresent(
    event?.title,
    event?.displayTitle,
    event?.displayName,
    event?.name,
    event?.label,
  )
  return typeof title === 'string' && title.trim() ? title.trim() : fallback
}

function formatWalkinEventFee(event) {
  if (event?.free === true) return 'Free'
  const pricing = firstPresent(event?.pricingDetail, event?.pricing, event?.priceObject, event?.priceDetails) || {}
  const fee = firstPresent(
    event?.discountedFee,
    event?.fee,
    event?.finalPrice,
    event?.discountedPrice,
    event?.introductoryPrice,
    event?.price,
    pricing?.discountedFee,
    pricing?.fee,
    pricing?.finalPrice,
    pricing?.discountedPrice,
    pricing?.introductoryPrice,
    pricing?.price,
  )
  return formatFeeValue(
    fee,
    firstPresent(event?.currencyId, event?.currency, pricing?.currencyId, pricing?.currency, event?.defaultCurrency),
    firstPresent(event?.currencySymbol, pricing?.currencySymbol),
  )
}

function formatPlanFee(plan) {
  if (plan?.free === true) return 'Free'
  const pricing = firstPresent(plan?.pricingDetail, plan?.pricing, plan?.priceObject, plan?.priceDetails) || {}
  const currencyId = firstPresent(plan?.currency, plan?.currencyId, plan?.defaultCurrency)
  const symbol = firstPresent(plan?.currencySymbol, currencyPrefix(currencyId))
  const returningFee = firstPresent(plan?.discountedPrice, plan?.finalPrice, pricing?.discountedPrice, pricing?.finalPrice)
  const firstVisitFee = firstPresent(plan?.introductoryPrice, pricing?.introductoryPrice)

  if (
    returningFee !== undefined &&
    returningFee !== null &&
    returningFee !== '' &&
    firstVisitFee !== undefined &&
    firstVisitFee !== null &&
    firstVisitFee !== '' &&
    String(returningFee) !== String(firstVisitFee)
  ) {
    return `Returning ${formatFeeValue(returningFee, currencyId, symbol)} / first visit ${formatFeeValue(
      firstVisitFee,
      currencyId,
      symbol,
    )}`
  }

  return formatFeeValue(
    firstPresent(
      plan?.discountedPrice,
      plan?.finalPrice,
      plan?.introductoryPrice,
      plan?.price,
      pricing?.discountedPrice,
      pricing?.finalPrice,
      pricing?.introductoryPrice,
      pricing?.price,
    ),
    firstPresent(currencyId, pricing?.currency, pricing?.currencyId),
    firstPresent(symbol, pricing?.currencySymbol),
  )
}

function eventDurationMinutes(eventDetail, referenceSlot) {
  const configuredDuration = Number(eventDetail?.durationInMins)
  if (Number.isFinite(configuredDuration) && configuredDuration > 0) return configuredDuration
  if (referenceSlot?.startTime && referenceSlot?.endTime) {
    const slotDuration = dayjs(referenceSlot.endTime).diff(dayjs(referenceSlot.startTime), 'minute')
    if (slotDuration > 0) return slotDuration
  }
  return 15
}

function profileDisplayName(profile) {
  if (!profile) return 'Patient'
  const rawName = (profile.displayName || profile.name || '').trim()
  const baseName = rawName.split(/\s(?:-|\u2014)\s/)[0]?.trim()
  if (profile.relation === 'SELF') {
    return profile.brandName ? `You - ${profile.brandName}` : 'You'
  }
  return baseName || profile.relation || 'Patient'
}

function writeLastPatientProfile(profileId) {
  try {
    if (profileId) localStorage.setItem(LAST_PATIENT_PROFILE_KEY, profileId)
  } catch {
    // no-op
  }
}

// Turns the booking-page endpoint's specific failure reason into a patient-facing line.
// The backend message already names the actual problem (no schedule configured, appointment
// type unpublished, practitioner not bookable, …); we just append the clinic-desk nudge.
function walkinUnavailableMessage(reason) {
  const detail = typeof reason?.message === 'string' ? reason.message.trim() : ''
  if (detail) {
    return /clinic desk/i.test(detail) ? detail : `${detail} Please ask the clinic desk.`
  }
  return 'This booking link has no active appointment types. Please ask the clinic desk.'
}

function buildUnavailableEvent(eventId) {
  return {
    eventId,
    title: UNAVAILABLE_EVENT_TITLE,
    failed: true,
    feeLabel: '',
    slots: [],
  }
}

export default function ClinicWalkinPage() {
  const navigate = useNavigate()
  const { token } = useParams()
  const { profileData, profileDataLoading, refreshProfileData } = useContext(ProfileContext)

  const [pageState, setPageState] = useState('resolving')
  const [resolveError, setResolveError] = useState('')
  const [resolveData, setResolveData] = useState(null)
  const [eventsById, setEventsById] = useState({})
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [feeByEventId, setFeeByEventId] = useState({})
  const [nextSlotsLoading, setNextSlotsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [bookingError, setBookingError] = useState(null)
  const [bookingNotice, setBookingNotice] = useState('')
  const [chosenSlot, setChosenSlot] = useState(null)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [initiatedBookingData, setInitiatedBookingData] = useState()
  const [bookedEventData, setBookedEventData] = useState()
  const [patientProfiles, setPatientProfiles] = useState([])
  const [patientProfilesLoading, setPatientProfilesLoading] = useState(false)
  const [patientProfilesError, setPatientProfilesError] = useState('')
  const [selectedPatientId, setSelectedPatientId] = useState('')
  const [loggedIn, setLoggedIn] = useState(() => !isTokenInvalidOrAbsent(localStorage.getItem('token')))
  const pollRef = useRef(null)

  const fetchEventBookingPage = useCallback(async (data, eventId) => {
    const from = dayjs().format('YYYY-MM-DD')
    const to = dayjs().endOf('month').format('YYYY-MM-DD')
    const timeZone = encodeURIComponent(getTimezoneOffset())
    let query =
      `mentorSlug=${encodeURIComponent(data.mentorSlug)}` +
      `&eventId=${encodeURIComponent(eventId)}` +
      `&from=${from}&to=${to}&timeZone=${timeZone}`
    if (data.clinicLocationId) query += `&clinicId=${encodeURIComponent(data.clinicLocationId)}`
    query += `&bookingSource=${encodeURIComponent(WALKIN_BOOKING_SOURCE)}`

    const response = unwrapResponse(
      await getPublicDataWithoutParams(`/api/public/clinic-booking-page?${query}`),
    )
    const body = response?.data
    // The backend returns a distinct code + message per failure mode (event unpublished,
    // no schedule configured, practitioner not bookable, …). Keep it so the caller can show
    // the actual reason instead of a single opaque "no appointment types" message.
    if (response?.status !== 200) {
      return { item: null, error: { code: body?.code, message: body?.message } }
    }
    const bookingPage = (body?.data ?? body)?.bookingPage
    if (!bookingPage?.event) {
      return { item: null, error: { code: body?.code, message: body?.message } }
    }

    const eventDetail = {
      ...bookingPage.event,
      contextId: bookingPage.event.contextId || data.clinicLocationId,
      clinicBrandId: bookingPage.event.clinicBrandId || data.clinicBrandId,
      bookingSource: WALKIN_BOOKING_SOURCE,
      useClinicServices:
        bookingPage.event?.useClinicServices === true ||
        String(bookingPage.event?.subscriptionPlanId || '').startsWith('CSPN-'),
    }
    const slots = Array.isArray(bookingPage.availableSlots)
      ? bookingPage.availableSlots.map(({ date, slots: s }) => ({
          date,
          availableTimeSlots: Array.isArray(s) ? s : [],
        }))
      : []

    return {
      item: {
        eventId,
        title: getWalkinEventTitle(bookingPage.event),
        eventDetail,
        feeLabel: formatWalkinEventFee(eventDetail),
        slots,
      },
      error: null,
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      setPageState('resolving')
      setResolveError('')
      setEventsById({})
      setSelectedEventId(null)

      const response = await postDataWithoutParams('/api/public/clinic-walkin/resolve', { token })
      if (cancelled) return

      const status = response?.status ?? response?.response?.status
      const body = response?.data ?? response?.response?.data
      if (status !== 200 || !isCommonResponseSuccessful(body?.code)) {
        if (body?.code === CODE_EXPIRED) {
          setResolveError(
            body?.message || 'This QR has expired. Please scan the latest QR shown at the clinic.',
          )
          setPageState('expired')
        } else {
          setResolveError(
            body?.message || 'This booking link is not valid. Please scan the QR at the clinic again.',
          )
          setPageState('invalid')
        }
        return
      }

      const data = body?.data ?? body
      setResolveData(data)
      const ids = [data?.defaultEventId, ...(data?.specialEventIds || [])].filter(Boolean)
      const primaryEventId = data?.defaultEventId || ids[0]
      const backgroundIds = ids.filter((id) => id !== primaryEventId)
      const primaryResult = primaryEventId ? await fetchEventBookingPage(data, primaryEventId) : null
      if (cancelled) return

      const primaryItem = primaryResult?.item
      if (primaryItem) {
        setEventsById({ [primaryItem.eventId]: primaryItem })
        setSelectedEventId(primaryItem.eventId)
        setPageState('booking')
        if (backgroundIds.length > 0) {
          Promise.all(backgroundIds.map((eventId) => fetchEventBookingPage(data, eventId))).then((results) => {
            if (cancelled) return
            setEventsById((prev) => {
              const next = { ...prev }
              backgroundIds.forEach((eventId, index) => {
                next[eventId] = results[index]?.item || buildUnavailableEvent(eventId)
              })
              return next
            })
          })
        }
        return
      }

      const fallbackResults = await Promise.all(backgroundIds.map((eventId) => fetchEventBookingPage(data, eventId)))
      if (cancelled) return
      const firstFallback = fallbackResults.find((result) => result?.item)?.item
      if (firstFallback) {
        const map = primaryEventId ? { [primaryEventId]: buildUnavailableEvent(primaryEventId) } : {}
        backgroundIds.forEach((eventId, index) => {
          map[eventId] = fallbackResults[index]?.item || buildUnavailableEvent(eventId)
        })
        setEventsById(map)
        setSelectedEventId(firstFallback.eventId)
        setPageState('booking')
      } else {
        // Surface the actual reason the booking page is empty (e.g. the practitioner has no
        // schedule configured, or the appointment type is unpublished) instead of always
        // showing the same generic message.
        const reason = primaryResult?.error || fallbackResults.find((result) => result?.error)?.error
        setResolveError(walkinUnavailableMessage(reason))
        setPageState('invalid')
      }
    }

    if (token) resolve()
    return () => {
      cancelled = true
    }
  }, [fetchEventBookingPage, token])

  useEffect(() => {
    if (pageState !== 'booking' || !resolveData || !selectedEventId) return undefined

    async function refresh() {
      setNextSlotsLoading(true)
      try {
        const result = await fetchEventBookingPage(resolveData, selectedEventId)
        if (result?.item) {
          setEventsById((prev) => ({ ...prev, [selectedEventId]: result.item }))
        }
      } finally {
        setNextSlotsLoading(false)
      }
    }

    pollRef.current = setInterval(refresh, 20000)
    return () => clearInterval(pollRef.current)
  }, [fetchEventBookingPage, pageState, resolveData, selectedEventId])

  const eventOptions = useMemo(
    () =>
      resolveData
        ? [resolveData.defaultEventId, ...(resolveData.specialEventIds || [])].filter(Boolean)
        : [],
    [resolveData],
  )
  const eventOptionsLoading = eventOptions.some((id) => eventsById[id] === undefined)
  const selectedEvent = selectedEventId ? eventsById[selectedEventId] : null
  const todayKey = dayjs().format('YYYY-MM-DD')
  const bookableSlot = useMemo(() => {
    const todaysSlots =
      selectedEvent?.slots?.find((item) => item?.date === todayKey)?.availableTimeSlots || []
    return getWalkinBookableSlot(todaysSlots)
  }, [selectedEvent?.slots, todayKey])
  const selectedFeeLabel = selectedEvent?.feeLabel || feeByEventId[selectedEventId] || ''
  const selectedFeeLoading =
    selectedEvent?.eventDetail?.subscriptionPlanId &&
    !selectedFeeLabel &&
    feeByEventId[selectedEventId] === undefined
  const selectedPatient = patientProfiles.find((profile) => profile?.id === selectedPatientId)
  const selectedEventTitle = selectedEvent
    ? getWalkinEventTitle(selectedEvent.eventDetail, selectedEvent.title || UNAVAILABLE_EVENT_TITLE)
    : 'Loading appointment type...'

  function getEventOptionLabel(eventId) {
    const eventItem = eventsById[eventId]
    if (!eventItem) return 'Loading appointment type...'
    const title = getWalkinEventTitle(
      eventItem.eventDetail,
      eventItem.title || UNAVAILABLE_EVENT_TITLE,
    )
    const feeLabel = eventItem.feeLabel || feeByEventId[eventId]
    return feeLabel ? `${title} - ${feeLabel}` : title
  }

  useEffect(() => {
    let cancelled = false

    async function fetchMissingFees() {
      const eventsNeedingFee = Object.values(eventsById).filter(
        (eventItem) =>
          eventItem?.eventId &&
          eventItem?.eventDetail &&
          !eventItem?.feeLabel &&
          feeByEventId[eventItem.eventId] === undefined,
      )
      if (eventsNeedingFee.length === 0) return

      const feeEntries = await Promise.all(
        eventsNeedingFee.map(async (eventItem) => {
          const planId = eventItem.eventDetail.subscriptionPlanId
          if (!planId || eventItem.eventDetail.useClinicServices) {
            return [eventItem.eventId, '']
          }

          const response = await getDataWithoutParams(
            `/api/subscription-plan?id=${planId}&zoneOffset=${encodeURIComponent(
              getTimezoneOffset(),
            )}&appVersion=${appVersion}&platformType=${platformWeb}&languageCode=${sessionStorage.getItem(
              'languageCode',
            )}&platform=${platformWeb}&countryCode=${getCountryCode()}`,
          )
          if (response?.status === 200 && response?.data) {
            return [eventItem.eventId, formatPlanFee(response?.data?.data ?? response?.data)]
          }
          return [eventItem.eventId, '']
        }),
      )

      if (cancelled) return
      setFeeByEventId((prev) => {
        const next = { ...prev }
        feeEntries.forEach(([eventId, feeLabel]) => {
          next[eventId] = feeLabel
        })
        return next
      })
    }

    fetchMissingFees()
    return () => {
      cancelled = true
    }
  }, [eventsById, feeByEventId])

  const loadPatientProfiles = useCallback(async () => {
    if (!loggedIn) return
    setPatientProfilesLoading(true)
    setPatientProfilesError('')
    try {
      const { listMyProfiles } = await import('src/patient/services/profilesService')
      const profiles = await listMyProfiles()
      const list = Array.isArray(profiles) ? profiles : []
      const clinicBrandId = resolveData?.clinicBrandId
      setPatientProfiles(list)
      setSelectedPatientId((current) => {
        const preferred =
          list.find((profile) => profile?.id === current) ||
          list.find((profile) => clinicBrandId && profile?.brandId === clinicBrandId) ||
          list.find((profile) => profile?.relation === 'SELF') ||
          list[0]
        if (preferred?.id) {
          writeLastPatientProfile(preferred.id)
          return preferred.id
        }
        return current || ''
      })
    } catch (error) {
      setPatientProfiles([])
      setPatientProfilesError('Could not load patient profiles. Booking can still continue.')
    } finally {
      setPatientProfilesLoading(false)
    }
  }, [loggedIn, resolveData?.clinicBrandId])

  useEffect(() => {
    if (pageState === 'booking' && loggedIn) {
      loadPatientProfiles()
    }
  }, [loadPatientProfiles, loggedIn, pageState])

  async function proceedWithBooking(profileToUse) {
    setBookingNotice('')
    if (!bookableSlot?.startTime || !bookableSlot?.endTime) {
      setBookingError('No bookable slot is available right now. Please ask the clinic desk.')
      return
    }
    if (!selectedEvent?.eventDetail) {
      setBookingError('Could not load this session. Please try again.')
      return
    }
    if (patientProfiles.length > 0 && !selectedPatientId) {
      setBookingError('Please choose the patient for this appointment.')
      return
    }

    // Canonical EventBookingReference shape — the purchase API binds
    // references to a List<EventBookingReference>, so this must be an array
    // of { refType, refId, customMap } objects, never a bare context object.
    const patientReference = selectedPatient
      ? {
          refType: 'patient-profile',
          refId: selectedPatient.id,
          customMap: {
            patientId: selectedPatient.id,
            patientProfileId: selectedPatient.id,
            patientName: profileDisplayName(selectedPatient),
            patientNumber: selectedPatient.patientNumber || null,
            relation: selectedPatient.relation || null,
            clinicBrandId: selectedPatient.brandId || resolveData?.clinicBrandId || null,
          },
        }
      : null

    setBookingError(null)
    setChosenSlot(bookableSlot)
    setPaymentLoading(true)
    await payAndMakeEventBooking(
      selectedEvent.eventDetail,
      {
        data: { startTime: bookableSlot.startTime, endTime: bookableSlot.endTime },
        postBufferMins: 0,
        preBufferMins: 0,
      },
      'razorpay',
      'GFT-default',
      null,
      resolveData?.mentorId,
      profileToUse,
      () => null,
      (data) => {
        if (data) setInitiatedBookingData(data)
        setPageState('confirmed')
      },
      'uid',
      'bookNextAvailable',
      (data) => setInitiatedBookingData(data),
      patientReference ? [patientReference] : null,
      selectedPatient ? { patientId: selectedPatient.id } : {},
    )
    setPaymentLoading(false)
  }

  async function handleBookButtonClick() {
    setBookingNotice('')
    if (isTokenInvalidOrAbsent(localStorage.getItem('token'))) {
      setShowClubbedAuthModal(true)
      return
    }
    if (profileDataLoading || !profileData) {
      setBookingError('Just a moment, loading your account details.')
      if (typeof refreshProfileData === 'function') {
        refreshProfileData({ _forceRefresh: true })
      }
      return
    }
    await proceedWithBooking(profileData)
  }

  async function handleAuthenticated() {
    try {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
      sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
      sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
      localStorage.removeItem('lastChosenFlow')
    } catch {
      // private-browsing: handled downstream
    }
    setShowClubbedAuthModal(false)
    setLoggedIn(true)
    try {
      if (typeof refreshProfileData === 'function') {
        refreshProfileData({ _forceRefresh: true })
      }
    } catch (e) {
      console.warn('Walk-in: refreshProfileData failed (non-fatal):', e?.message)
    }
    setBookingError(null)
    setBookingNotice('Mobile verified. Please review the appointment type, then tap Pay and Book.')
  }

  function handleWalkinLogout() {
    logoutHandler()
    setLoggedIn(false)
    setPatientProfiles([])
    setPatientProfilesError('')
    setSelectedPatientId('')
    setShowClubbedAuthModal(false)
    setBookingError(null)
    setBookingNotice('Signed out. Continue with mobile to book for a different patient.')
  }

  function renderWalkinLogoutButton() {
    if (!loggedIn) return null
    return (
      <button
        type="button"
        onClick={handleWalkinLogout}
        aria-label="Logout of current account"
        style={{
          minHeight: 32,
          padding: '7px 12px',
          borderRadius: 8,
          border: '1px solid #d1d5db',
          background: '#ffffff',
          color: '#374151',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Logout
      </button>
    )
  }

  useEffect(() => {
    if (pageState !== 'confirmed' || bookedEventData) return undefined
    const bookedEventId = initiatedBookingData?.bookedEvent?.id
    if (!bookedEventId) return undefined

    let cancelled = false
    async function getAndSetBookedEventData() {
      for (let attempt = 0; attempt < 5; attempt++) {
        const res = await getDataWithoutParams(
          `/api/secure/user/booked-slot/details?eventId=${bookedEventId}`,
        )
        if (cancelled) return
        if (res?.status === 200 && isCommonResponseSuccessful(res?.data?.code)) {
          const fetched = res?.data?.data?.bookedEvent
          setBookedEventData(fetched)
          if (fetched?.appointmentNumber) return
        }
        await new Promise((r) => setTimeout(r, 1500))
      }
    }

    getAndSetBookedEventData()
    return () => {
      cancelled = true
    }
  }, [bookedEventData, initiatedBookingData?.bookedEvent?.id, pageState])

  useEffect(() => {
    if (pageState !== 'confirmed') return undefined
    const event = bookedEventData || initiatedBookingData?.bookedEvent
    const appointmentTime = event?.startTime || chosenSlot?.startTime
    saveClinicWalkinBookingHandoff({
      eventId: event?.id || initiatedBookingData?.bookedEvent?.id || null,
      appointmentNumber: bookedEventData?.appointmentNumber || event?.appointmentNumber || null,
      title: selectedEventTitle,
      practitionerName: resolveData?.mentorName || '',
      clinicName: resolveData?.clinicName || '',
      scheduledAt: appointmentTime || null,
      durationMinutes: eventDurationMinutes(selectedEvent?.eventDetail, chosenSlot),
      patientProfileId: selectedPatientId || null,
      patientName: selectedPatient ? profileDisplayName(selectedPatient) : profileData?.name || null,
      locationName: resolveData?.clinicName || '',
      sessionMode: 'OFFLINE',
    })

    const timer = setTimeout(() => {
      navigate('/patient-home')
    }, 2600)
    return () => clearTimeout(timer)
  }, [
    bookedEventData,
    chosenSlot,
    initiatedBookingData,
    navigate,
    pageState,
    profileData?.name,
    resolveData?.clinicName,
    resolveData?.mentorName,
    selectedEvent,
    selectedEventTitle,
    selectedPatient,
    selectedPatientId,
  ])

  function resetForNextPatient() {
    setBookedEventData(undefined)
    setInitiatedBookingData(undefined)
    setChosenSlot(null)
    setBookingError(null)
    setBookingNotice('')
    setSelectedEventId(resolveData?.defaultEventId)
    setPageState('booking')
  }

  return (
    <>
      {showClubbedAuthModal && (
        <Suspense fallback={<LoadingSpinner bgColor="#F5F5F599" dark />}>
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => setShowClubbedAuthModal(false)}
            loginVariant={false}
            nameFieldHeading="Patient Name"
            preferredLoginInputType="mobile"
            preferredCountryCode="in"
            compactAuthMode
            allowMobileRegistration
            onAuthenticated={handleAuthenticated}
          />
        </Suspense>
      )}

      {pageState === 'resolving' && <WalkinResolvingState />}

      {(pageState === 'invalid' || pageState === 'expired') && (
        <WalkinFrame centered>
          {loggedIn && (
            <div style={{ width: '100%', maxWidth: 420, display: 'flex', justifyContent: 'flex-end' }}>
              {renderWalkinLogoutButton()}
            </div>
          )}
          <div style={{ fontWeight: 700, marginTop: 40, textAlign: 'center' }}>
            {pageState === 'expired' ? 'QR expired' : 'Invalid link'}
          </div>
          <div style={{ marginTop: 10, textAlign: 'center', maxWidth: 320, fontSize: 14 }}>
            {resolveError}
          </div>
        </WalkinFrame>
      )}

      {pageState === 'booking' && resolveData && (
        <WalkinFrame>
          <div style={{ width: '100%', maxWidth: 420, padding: '8px 0 28px' }}>
            <div
              style={{
                minHeight: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: loggedIn ? 'space-between' : 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  minWidth: 0,
                  color: '#6b7280',
                  fontSize: 13,
                  textAlign: loggedIn ? 'left' : 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {resolveData.clinicName}
              </div>
              {renderWalkinLogoutButton()}
            </div>

            <section
              style={{
                marginTop: 12,
                padding: 16,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#ffffff',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {resolveData.mentorProfilePicId && (
                  <img
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                    src={getImageLink(resolveData.mentorProfilePicId)}
                    alt="Practitioner"
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Booking with</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
                    {resolveData.mentorName}
                  </div>
                  <div style={{ fontSize: 14, color: '#374151', marginTop: 4 }}>
                    {selectedEventTitle}
                  </div>
                </div>
              </div>

              {(selectedFeeLabel || selectedFeeLoading) && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: '#f3f4f6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 14,
                  }}
                >
                  <span style={{ color: '#6b7280' }}>Fee</span>
                  <strong style={{ color: '#111827', textAlign: 'right' }}>
                    {selectedFeeLoading ? 'Loading fee...' : selectedFeeLabel}
                  </strong>
                </div>
              )}
            </section>

            {eventOptions.length > 1 && (
              <div style={{ marginTop: 14 }}>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                  Session type
                </label>
                <select
                  value={selectedEventId || ''}
                  onChange={(e) => {
                    setBookingError(null)
                    setBookingNotice('')
                    setSelectedEventId(e.target.value)
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                  }}
                >
                  {eventOptions.map((id) => (
                    <option key={id} value={id}>
                      {getEventOptionLabel(id)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <section
              style={{
                marginTop: 14,
                padding: 16,
                borderRadius: 8,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 18 }}>{dayjs().format('DD-MMM-YYYY')}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Today</div>
              <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                Next available time {nextSlotsLoading && <Spinner animation="border" variant="dark" size="sm" />}
              </div>
              {bookableSlot ? (
                <div style={{ fontSize: 30, fontWeight: 800, color: '#111827', marginTop: 2 }}>
                  {bookableSlot.immediate ? 'Now' : dayjs(bookableSlot.startTime).format('h:mm A')}
                  {bookableSlot.immediate && (
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#6b7280' }}>
                      {dayjs(bookableSlot.startTime).format('h:mm A')}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, marginTop: 8 }}>
                  {nextSlotsLoading ? (
                    <Spinner animation="border" variant="dark" size="sm" />
                  ) : (
                    'No slots available right now'
                  )}
                </div>
              )}
            </section>

            {loggedIn && (
              <section
                style={{
                  marginTop: 14,
                  padding: 16,
                  borderRadius: 8,
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                  Patient for this appointment
                </div>
                {patientProfilesLoading ? (
                  <Spinner animation="border" variant="dark" size="sm" />
                ) : patientProfiles.length > 0 ? (
                  <>
                    <select
                      value={selectedPatientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value)
                        writeLastPatientProfile(e.target.value)
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: '#fff',
                      }}
                    >
                      {patientProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profileDisplayName(profile)}
                          {profile.patientNumber ? ` - #${profile.patientNumber}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => navigate('/patient-home/profile/family')}
                      style={{
                        marginTop: 10,
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                        color: '#514387',
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      Add new patient
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {patientProfilesError || `Continuing as ${profileData?.name || 'this account'}.`}
                  </div>
                )}
              </section>
            )}

            <CPBPOMBookAndPayButton
              style={{ marginTop: 16, width: '100%' }}
              onClick={handleBookButtonClick}
              disabled={
                paymentLoading ||
                eventOptionsLoading ||
                !selectedEvent?.eventDetail ||
                !bookableSlot ||
                (loggedIn && profileDataLoading)
              }
            >
              {loggedIn ? 'Pay and Book' : 'Continue with mobile'}
              {paymentLoading && <Spinner animation="border" variant="light" size="sm" />}
            </CPBPOMBookAndPayButton>

            {bookingError && (
              <div style={{ color: '#c0392b', fontSize: 13, marginTop: 10, textAlign: 'center' }}>
                {bookingError}
              </div>
            )}
            {bookingNotice && (
              <div style={{ color: '#166534', fontSize: 13, marginTop: 10, textAlign: 'center' }}>
                {bookingNotice}
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
              Appointments may be delayed due to existing appointments or emergencies.
            </div>
          </div>
        </WalkinFrame>
      )}

      {pageState === 'confirmed' && (
        <WalkinFrame>
          <div style={{ width: '100%', maxWidth: 420, padding: '32px 0', textAlign: 'center' }}>
            <div
              aria-hidden="true"
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 16px',
                borderRadius: '50%',
                background: '#dcfce7',
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 34,
                fontWeight: 800,
              }}
            >
              ✓
            </div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>Booking successful</div>
            <div style={{ marginTop: 8, fontSize: 14, color: '#4b5563' }}>
              Your appointment has been confirmed. We are opening your patient portal now.
            </div>
            <section
              style={{
                marginTop: 18,
                padding: 16,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#fff',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 700 }}>{selectedEventTitle}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                {resolveData?.mentorName}
                {chosenSlot?.startTime ? ` - ${dayjs(chosenSlot.startTime).format('h:mm A')}` : ''}
              </div>
              {bookedEventData?.appointmentNumber && (
                <div style={{ marginTop: 14, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Appointment number</div>
                  <div style={{ fontSize: 44, fontWeight: 900, color: '#111827' }}>
                    {bookedEventData.appointmentNumber}
                  </div>
                </div>
              )}
            </section>
            <CPBPOMBookAndPayButton
              style={{ marginTop: 18, width: '100%' }}
              onClick={() => navigate('/patient-home')}
            >
              Open patient portal
            </CPBPOMBookAndPayButton>
            <button
              type="button"
              onClick={resetForNextPatient}
              style={{
                marginTop: 12,
                border: 0,
                background: 'transparent',
                color: '#514387',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Book next patient
            </button>
          </div>
        </WalkinFrame>
      )}
    </>
  )
}
